import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, Plus, Trash2, Save, Edit, Users } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ACCENT_COLORS = [
  { value: '#6366F1', label: '紫色' },
  { value: '#10B981', label: '綠色' },
  { value: '#F59E0B', label: '橙色' },
  { value: '#EF4444', label: '紅色' },
  { value: '#3B82F6', label: '藍色' },
  { value: '#EC4899', label: '粉色' },
  { value: '#8B5CF6', label: '薰衣草' },
  { value: '#14B8A6', label: '青色' },
];

const AdminSubAgents = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { agentId } = useParams();
  const [agent, setAgent] = useState(null);
  const [subagents, setSubagents] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [openrouterModels, setOpenrouterModels] = useState([]);
  const [loadingModels, setLoadingModels] = useState(false);

  const emptyForm = {
    name: '',
    description: '',
    system_prompt: '',
    model_provider: 'openrouter',
    model_name: 'anthropic/claude-sonnet-4-5-20250929',
    temperature: 0.7,
    enable_reasoning: false,
    reasoning_level: 'medium',
    color: '#6366F1',
    status: 'active',
  };
  const [formData, setFormData] = useState(emptyForm);

  useEffect(() => {
    if (user?.role !== 'admin') { navigate('/create'); return; }
    fetchAgent();
    fetchSubagents();
    fetchModels();
  }, [user, navigate, agentId]);

  const fetchAgent = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/agents/${agentId}`);
      setAgent(res.data);
    } catch { toast.error('載入 Agent 失敗'); }
  };

  const fetchSubagents = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/agents/${agentId}/subagents`);
      setSubagents(res.data);
    } catch { toast.error('載入 Sub-agents 失敗'); }
  };

  const fetchModels = async () => {
    setLoadingModels(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/openrouter-models`);
      setOpenrouterModels(res.data);
    } catch { /* ignore */ }
    finally { setLoadingModels(false); }
  };

  const handleCreate = async () => {
    if (!formData.name.trim()) { toast.error('名稱不能為空'); return; }
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/admin/agents/${agentId}/subagents`, formData);
      toast.success('Sub-agent 已新增');
      setShowCreateForm(false);
      setFormData(emptyForm);
      fetchSubagents();
    } catch { toast.error('新增失敗'); }
    finally { setLoading(false); }
  };

  const handleUpdate = async (subId) => {
    setLoading(true);
    try {
      await axios.put(`${API_URL}/api/admin/agents/${agentId}/subagents/${subId}`, formData);
      toast.success('已更新');
      setEditingId(null);
      setFormData(emptyForm);
      fetchSubagents();
    } catch { toast.error('更新失敗'); }
    finally { setLoading(false); }
  };

  const handleDelete = async (subId) => {
    if (!window.confirm('確定要刪除這個 Sub-agent？')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/agents/${agentId}/subagents/${subId}`);
      toast.success('已刪除');
      fetchSubagents();
    } catch { toast.error('刪除失敗'); }
  };

  const startEdit = (sub) => {
    setEditingId(sub.id);
    setFormData({
      name: sub.name,
      description: sub.description,
      system_prompt: sub.system_prompt,
      model_provider: sub.model_provider,
      model_name: sub.model_name,
      temperature: sub.temperature ?? 0.7,
      enable_reasoning: sub.enable_reasoning || false,
      reasoning_level: sub.reasoning_level || 'medium',
      color: sub.color || '#6366F1',
      status: sub.status || 'active',
    });
  };

  if (user?.role !== 'admin') return null;

  return (
    <>
      <header className="bg-white border-b border-neutral-200 px-8 py-6 flex-shrink-0">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin/agents')} className="rounded-full" data-testid="back-to-agents-btn">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-heading font-bold text-neutral-900">
              {agent ? `${agent.icon} ${agent.name}` : '...'} — Sub-agents 管理
            </h1>
            <p className="text-neutral-600 text-sm mt-1">管理此 Agent 下的協作助手，用戶可在對話中用 @ 呼叫</p>
          </div>
        </div>
      </header>

      <main className="flex-1 overflow-y-auto p-8">
        <div className="flex justify-end mb-6">
          <Button onClick={() => { setShowCreateForm(true); setFormData(emptyForm); }} className="rounded-full" data-testid="create-subagent-btn">
            <Plus className="w-4 h-4 mr-2" />
            新增 Sub-agent
          </Button>
        </div>

        {showCreateForm && (
          <Card className="p-6 mb-6 border-2 border-primary-200" data-testid="create-subagent-form">
            <h2 className="text-lg font-heading font-bold mb-4">新增 Sub-agent</h2>
            <SubAgentForm formData={formData} setFormData={setFormData} models={openrouterModels} loadingModels={loadingModels}
              onSave={handleCreate} onCancel={() => { setShowCreateForm(false); setFormData(emptyForm); }} loading={loading} />
          </Card>
        )}

        <div className="space-y-4">
          {subagents.length === 0 && !showCreateForm && (
            <div className="text-center py-16 text-neutral-400">
              <Users className="w-12 h-12 mx-auto mb-4 opacity-40" />
              <p className="text-lg font-medium">尚無 Sub-agent</p>
              <p className="text-sm mt-1">新增 Sub-agent 後，用戶可在對話中用 @ 呼叫它們</p>
            </div>
          )}

          {subagents.map((sub) => (
            <Card key={sub.id} className="p-5" data-testid={`subagent-card-${sub.id}`}>
              {editingId === sub.id ? (
                <div>
                  <h3 className="text-lg font-heading font-bold mb-4">編輯 Sub-agent</h3>
                  <SubAgentForm formData={formData} setFormData={setFormData} models={openrouterModels} loadingModels={loadingModels}
                    onSave={() => handleUpdate(sub.id)} onCancel={() => { setEditingId(null); setFormData(emptyForm); }} loading={loading} />
                </div>
              ) : (
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <div className="w-3 h-10 rounded-full flex-shrink-0" style={{ backgroundColor: sub.color || '#6366F1' }} />
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-bold text-neutral-900">{sub.name}</h3>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${sub.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-neutral-100 text-neutral-500'}`}>
                          {sub.status === 'active' ? '啟用' : '停用'}
                        </span>
                      </div>
                      <p className="text-sm text-neutral-500 mt-0.5">{sub.description}</p>
                      <div className="flex gap-4 mt-2 text-xs text-neutral-400">
                        <span>模型: {sub.model_name}</span>
                        <span>溫度: {sub.temperature}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-1.5">
                    <Button variant="outline" size="sm" onClick={() => startEdit(sub)} className="rounded-full" data-testid={`edit-subagent-${sub.id}`}>
                      <Edit className="w-3.5 h-3.5" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(sub.id)} className="rounded-full text-red-500" data-testid={`delete-subagent-${sub.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              )}
            </Card>
          ))}
        </div>
      </main>
    </>
  );
};

const SubAgentForm = ({ formData, setFormData, models, loadingModels, onSave, onCancel, loading }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const filteredModels = models.filter(m =>
    m.name.toLowerCase().includes(searchTerm.toLowerCase()) || m.id.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-5">
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <Label>名稱（用戶 @ 時使用）</Label>
          <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="例：翻譯助手" data-testid="subagent-name-input" />
          <p className="text-xs text-neutral-500 mt-1">用戶輸入 @{formData.name || '名稱'} 即可呼叫</p>
        </div>
        <div>
          <Label>功能說明（前台顯示）</Label>
          <Input value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder="簡短描述這個助手的功能" data-testid="subagent-desc-input" />
        </div>
      </div>

      <div>
        <Label>System Prompt</Label>
        <Textarea value={formData.system_prompt} onChange={(e) => setFormData({ ...formData, system_prompt: e.target.value })}
          className="min-h-[150px] font-mono text-sm" placeholder="輸入此 Sub-agent 的專業指令..." data-testid="subagent-prompt-input" />
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <div>
          <Label>AI 模型 {loadingModels && '(載入中...)'}</Label>
          <Input placeholder="搜尋模型..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="mb-2" />
          <Select value={formData.model_name} onValueChange={(v) => setFormData({ ...formData, model_name: v, model_provider: 'openrouter' })}>
            <SelectTrigger className="bg-white"><SelectValue placeholder="選擇模型..." /></SelectTrigger>
            <SelectContent className="max-h-[300px] bg-white">
              {filteredModels.map(m => (
                <SelectItem key={m.id} value={m.id} className="bg-white hover:bg-primary-50">
                  <span className="text-sm">{m.name}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Temperature</Label>
          <Input type="number" min="0" max="2" step="0.1" value={formData.temperature}
            onChange={(e) => setFormData({ ...formData, temperature: parseFloat(e.target.value) || 0.7 })} data-testid="subagent-temp-input" />
        </div>
        <div>
          <Label>標識顏色</Label>
          <div className="flex flex-wrap gap-2 mt-1.5">
            {ACCENT_COLORS.map(c => (
              <button key={c.value} onClick={() => setFormData({ ...formData, color: c.value })}
                className={`w-7 h-7 rounded-full border-2 transition-all ${formData.color === c.value ? 'border-neutral-900 scale-110' : 'border-transparent'}`}
                style={{ backgroundColor: c.value }} title={c.label} data-testid={`color-${c.value}`} />
            ))}
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <Label>推理功能</Label>
          <Select value={formData.enable_reasoning ? 'on' : 'off'} onValueChange={(v) => setFormData({ ...formData, enable_reasoning: v === 'on' })}>
            <SelectTrigger className="bg-white"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="off" className="bg-white hover:bg-primary-50">停用</SelectItem>
              <SelectItem value="on" className="bg-white hover:bg-primary-50">啟用</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {formData.enable_reasoning && (
          <div>
            <Label>推理等級</Label>
            <Select value={formData.reasoning_level} onValueChange={(v) => setFormData({ ...formData, reasoning_level: v })}>
              <SelectTrigger className="bg-white"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="low" className="bg-white hover:bg-primary-50">低</SelectItem>
                <SelectItem value="medium" className="bg-white hover:bg-primary-50">中</SelectItem>
                <SelectItem value="high" className="bg-white hover:bg-primary-50">高</SelectItem>
                <SelectItem value="xhigh" className="bg-white hover:bg-primary-50">超高 (xhigh · Opus 4.7+)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      <div>
        <Label>狀態</Label>
        <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
          <SelectTrigger className="bg-white w-40"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="active" className="bg-white hover:bg-primary-50">啟用</SelectItem>
            <SelectItem value="inactive" className="bg-white hover:bg-primary-50">停用</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="flex gap-2 pt-2">
        <Button onClick={onSave} disabled={loading} className="rounded-full" data-testid="save-subagent-btn">
          <Save className="w-4 h-4 mr-2" />{loading ? '處理中...' : '儲存'}
        </Button>
        <Button variant="outline" onClick={onCancel} className="rounded-full">取消</Button>
      </div>
    </div>
  );
};

export default AdminSubAgents;
